import ImportHistoryModal from '@/components/ImportHistory/Modal';
import Constants from '@/Constants';
import { useGet } from '@/hooks/useGet';
import { EnrollmentProcess, ImportStatus } from '@/models/EnrollmentProcess';
import { ProgramClass } from '@/models/ProgramClass';
import { momentWithTimeZone } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import './style.scss';

const processingStatusList: ImportStatus[] = [ImportStatus.NOT_STARTED, ImportStatus.STARTED];
const TIME_TO_REFRESH: number = 30;

interface Props {
    onRefresh: () => void;
    programClass: ProgramClass;
}

const ImportHistory: React.FC<Props> = (props: Props) => {
    const { onRefresh, programClass } = props;
    const { strings, success } = useSelector((state: ApplicationState) => ({
        success: state.programClass.success,
        strings: state.locale.data,
    }));

    const { data, revalidateResult, loading, isValidating } = useGet<EnrollmentProcess[]>(
        programClass.id ? Constants.LMS_PROGRAM_CLASS_MASS_IMPORT_URI.replace('%p', String(programClass.id)) : null,
    );

    const [refreshIn, setRefreshIn] = useState<number>(TIME_TO_REFRESH);
    const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout>();
    const [selectedItem, setSelectedItem] = useState<EnrollmentProcess | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);

    const isProcessingImport: boolean = !!data?.some((process: EnrollmentProcess) => processingStatusList.includes(process.status));

    const isRefreshing: boolean = loading || isValidating;

    const enrollmentsProcesses: EnrollmentProcess[] = data ? [...data].reverse() : [];

    const getImportHistory = (): void => {
        if (programClass.id) {
            revalidateResult();
        }
    };

    const startInterval = (): void => {
        setRefreshInterval(
            setInterval(() => {
                setRefreshIn((value: number) => (value === 0 ? 0 : isRefreshing ? value : value - 1));
            }, 1000),
        );
    };

    const stopInterval = (): void => {
        if (refreshInterval) {
            clearInterval(refreshInterval);
        }
    };

    const getStatusDescription = (status: ImportStatus): string => {
        switch (status) {
            case ImportStatus.STARTED:
            case ImportStatus.NOT_STARTED:
                return strings.PROCESSING_IMPORT;
            case ImportStatus.COMPLETED_WITH_ERRORS:
                return strings.IMPORT_COMPLETED_WITH_ERROR;
            case ImportStatus.COMPLETED:
                return strings.IMPORT_COMPLETED;
            default:
                return '';
        }
    };

    const getNumberColValue = (value: number) => (value === 0 ? '-' : value);

    const getDateColValue = (value: Date) => (value ? momentWithTimeZone(value, strings._LANGUAGE, false, false, true) : '-');

    const openDetailModal = (item: EnrollmentProcess) => {
        setShowModal(true);
        setSelectedItem(item);
    };

    const closeDetailModal = () => {
        setShowModal(false);
        setSelectedItem(null);
    };

    useEffect(() => {
        return () => {
            stopInterval();
        };
    }, [programClass.id]);

    useEffect(() => {
        if (isProcessingImport) {
            startInterval();
        }

        return () => stopInterval();
    }, [isProcessingImport, isRefreshing]);

    useEffect(() => {
        if (refreshIn === 0) {
            getImportHistory();
            onRefresh();
        }
    }, [refreshIn]);

    useEffect(() => {
        if (success) {
            getImportHistory();
        }
    }, [success]);

    useEffect(() => {
        if (!isRefreshing) {
            setRefreshIn(TIME_TO_REFRESH);
        }
    }, [isRefreshing]);

    return (
        <>
            {selectedItem && <ImportHistoryModal onClose={closeDetailModal} processItem={selectedItem} show={showModal} />}

            {enrollmentsProcesses && enrollmentsProcesses.length > 0 && (
                <div className="import-history-container">
                    <header>
                        <p>{strings.ADMIN_IMPORT_USERS}</p>

                        {isProcessingImport && (
                            <div>
                                <div>
                                    {strings.THE_SCREEN_WILL_REFRESH_IN}
                                    <span>{refreshIn}</span>
                                </div>

                                <button
                                    disabled={isRefreshing}
                                    onClick={() => {
                                        stopInterval();
                                        getImportHistory();
                                        onRefresh();
                                    }}
                                >
                                    <i className="material-icons-outlined">{Constants.icons.REFRESH}</i>
                                </button>
                            </div>
                        )}
                    </header>

                    <table className="table table-borderless table-sm table-striped">
                        <thead>
                            <tr>
                                <th scope="col">{strings.START}</th>
                                <th scope="col">{strings.END}</th>
                                <th scope="col">{strings.CLASS_TOTAL_RECORDS}</th>
                                <th scope="col">{strings.SUCCESS}</th>
                                <th scope="col">{strings.FAILURE}</th>
                                <th scope="col">{strings.STATUS}</th>
                                <th scope="col">{strings.DETAILS}</th>
                            </tr>
                        </thead>

                        <tbody>
                            {enrollmentsProcesses.map((process: EnrollmentProcess) => (
                                <tr className={`table-row${processingStatusList.includes(process.status) ? ' processing' : ''}`} key={process.id}>
                                    <td scope="row">{getDateColValue(process.startDate)}</td>
                                    <td scope="row">{getDateColValue(process.finishDate)}</td>
                                    <td scope="row">{getNumberColValue(process.itemsCount)}</td>
                                    <td scope="row">{getNumberColValue(process.successItemsCount)}</td>
                                    <td scope="row">{getNumberColValue(process.errorItemsCount)}</td>
                                    <td className={process.status === ImportStatus.COMPLETED_WITH_ERRORS ? 'alert' : ''} scope="row">
                                        {getStatusDescription(process.status)}
                                    </td>
                                    <td scope="row">
                                        <button disabled={processingStatusList.includes(process.status)} onClick={() => openDetailModal(process)}>
                                            {strings.OPEN}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default ImportHistory;
